(function (root) {
  'use strict';

  function isoDate(date) {
    return date.toISOString().slice(0, 10);
  }

  function dateAtUTC(year, month, day) {
    return new Date(Date.UTC(year, month - 1, day));
  }

  function easterSunday(year) {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return dateAtUTC(year, month, day);
  }

  function nationalHolidays(year) {
    const dates = new Set();
    const add = (date) => dates.add(isoDate(date));
    for (const [month, day] of [[1, 1], [1, 26]]) {
      const date = dateAtUTC(year, month, day);
      add(date);
      if (date.getUTCDay() === 6) add(dateAtUTC(year, month, day + 2));
      if (date.getUTCDay() === 0) add(dateAtUTC(year, month, day + 1));
    }
    const easter = easterSunday(year);
    const friday = new Date(easter);
    friday.setUTCDate(friday.getUTCDate() - 2);
    const monday = new Date(easter);
    monday.setUTCDate(monday.getUTCDate() + 1);
    add(friday);
    add(monday);
    add(dateAtUTC(year, 4, 25));
    const christmas = dateAtUTC(year, 12, 25);
    const boxing = dateAtUTC(year, 12, 26);
    add(christmas);
    add(boxing);
    if ([0, 6].includes(christmas.getUTCDay())) add(dateAtUTC(year, 12, 27));
    if ([0, 6].includes(boxing.getUTCDay())) add(dateAtUTC(year, 12, 28));
    return dates;
  }

  function normaliseExtraHolidays(value) {
    const values = Array.isArray(value) ? value : String(value || '').split(/[\s,;]+/);
    return values.filter((day) => /^\d{4}-\d{2}-\d{2}$/.test(day) &&
      Number.isFinite(new Date(day + 'T00:00:00Z').getTime()) &&
      isoDate(new Date(day + 'T00:00:00Z')) === day);
  }

  function calculateArrival(now = new Date(), extraHolidays = []) {
    if (!Number.isFinite(now.getTime())) throw new RangeError('Invalid date');
    const parts = new Intl.DateTimeFormat('en-AU', {
      timeZone: 'Australia/Sydney', year: 'numeric', month: '2-digit', day: '2-digit'
    }).formatToParts(now);
    const part = (type) => Number(parts.find((entry) => entry.type === type).value);
    const cursor = dateAtUTC(part('year'), part('month'), part('day'));
    const extra = new Set(normaliseExtraHolidays(extraHolidays));
    const holidays = new Map();
    let businessDays = 0;
    while (businessDays < 2) {
      cursor.setUTCDate(cursor.getUTCDate() + 1);
      const year = cursor.getUTCFullYear();
      if (!holidays.has(year)) holidays.set(year, nationalHolidays(year));
      const day = cursor.getUTCDay();
      const iso = isoDate(cursor);
      if (day !== 0 && day !== 6 && !holidays.get(year).has(iso) && !extra.has(iso)) businessDays += 1;
    }
    return {
      iso: isoDate(cursor),
      display: new Intl.DateTimeFormat('en-AU', {
        timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long'
      }).format(cursor)
    };
  }

  function createQueue() {
    let pending = Promise.resolve();
    return function enqueue(task) {
      const next = pending.then(task);
      pending = next.catch(() => undefined);
      return next;
    };
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { calculateArrival, nationalHolidays, normaliseExtraHolidays, createQueue };
  }
  if (!root || !root.document) return;

  let config = { routesRoot: '/', locale: 'en-AU', currency: 'AUD', strings: {}, extraHolidays: [] };
  const configNode = root.document.getElementById('BrandStoreConfig');
  if (configNode) {
    try { config = Object.assign(config, JSON.parse(configNode.textContent)); } catch (_) {}
  }
  config.extraHolidays = normaliseExtraHolidays(config.extraHolidays);
  const enqueue = createQueue();
  const formatter = new Intl.NumberFormat(config.locale, { style: 'currency', currency: config.currency });

  function text(key, values = {}) {
    let value = config.strings[key] || key;
    Object.entries(values).forEach(([token, replacement]) => {
      value = value.split('{' + token + '}').join(String(replacement));
    });
    return value;
  }

  async function request(path, body) {
    const base = new URL(config.routesRoot, root.location.origin);
    const url = new URL(path, base);
    if (url.origin !== root.location.origin) throw new Error(text('request_error'));
    let response;
    try {
      response = await root.fetch(url.href, {
        method: body === undefined ? 'GET' : 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        ...(body === undefined ? {} : { body: JSON.stringify(body) })
      });
    } catch (_) {
      throw new Error(text('network_error'));
    }
    let data;
    try { data = await response.json(); } catch (_) { throw new Error(text('request_error')); }
    if (!response.ok) {
      const message = typeof data.description === 'string' ? data.description : data.message;
      throw new Error(typeof message === 'string' ? message : text('request_error'));
    }
    return data;
  }

  function hydrate(scope = root.document) {
    const nodes = Array.from(scope.querySelectorAll('[data-brand-arrival]'));
    if (scope.matches && scope.matches('[data-brand-arrival]')) nodes.push(scope);
    if (!nodes.length) return;
    try {
      const estimate = calculateArrival(new Date(), config.extraHolidays);
      nodes.forEach((node) => {
        node.textContent = estimate.display;
        node.setAttribute('datetime', estimate.iso);
        node.hidden = false;
        const row = node.closest('[data-brand-arrival-row]');
        if (row) row.hidden = false;
      });
    } catch (_) {
      nodes.forEach((node) => {
        const row = node.closest('[data-brand-arrival-row]');
        (row || node).hidden = true;
      });
    }
  }

  async function checkout(form) {
    if (form.dataset.brandCheckoutBusy === 'true') return false;
    form.dataset.brandCheckoutBusy = 'true';
    const error = form.querySelector('[data-brand-cart-error]');
    const fallback = form.querySelector('[data-brand-checkout-fallback]');
    const buttons = Array.from(form.querySelectorAll('[name="checkout"]:not([data-brand-checkout-fallback])'));
    buttons.forEach((button) => { button.disabled = true; });
    if (error) { error.textContent = ''; error.hidden = true; }
    try {
      await enqueue(async () => {
        const estimate = calculateArrival(new Date(), config.extraHolidays);
        await request('cart/update.js', { attributes: {
          brand_estimated_arrival: estimate.iso,
          brand_estimated_arrival_display: estimate.display
        } });
      });
      root.location.assign(new URL('checkout', new URL(config.routesRoot, root.location.origin)).href);
      return true;
    } catch (_) {
      if (error) { error.textContent = text('checkout_error'); error.hidden = false; }
      if (fallback) fallback.hidden = false;
      buttons.forEach((button) => { button.disabled = false; });
      form.dataset.brandCheckoutBusy = 'false';
      return false;
    }
  }

  root.BrandStore = {
    config, text, request, enqueue, hydrate, checkout,
    money: (cents) => formatter.format(Number(cents) / 100),
    arrival: (now = new Date()) => calculateArrival(now, config.extraHolidays)
  };
  hydrate();
  root.document.addEventListener('shopify:section:load', (event) => hydrate(event.target));
  root.document.addEventListener('visibilitychange', () => { if (!root.document.hidden) hydrate(); });
  root.setInterval(() => { if (!root.document.hidden) hydrate(); }, 60000);
})(typeof window !== 'undefined' ? window : undefined);
